/**
 * „Systemlisten" — der Überblick über die gebundenen Systemlisten (Weg 2):
 * Aufgaben, Prozesse, Projekte, CRM (Kontakte/Firmen/Personen/Kontakt-Typen/…),
 * Lesezeichen usw. Jede Systemliste trägt einen `list_type` (`<paket>.<liste>`);
 * gewöhnliche Listen (ohne list_type) erscheinen hier NICHT. Ein Klick öffnet
 * die jeweilige Liste im bestehenden Listen-Editor (der den Workspace der Datei
 * selbst auflöst).
 *
 * Zwei Modi (Redesign Welle R):
 *   - WORKSPACE-Scope: die Systemlisten des aktiven Workspace.
 *   - PRIVATBEREICH-Scope (`privateScope`): die **Cross-WS-Aggregation** über ALLE
 *     Workspaces des Nutzers (D-146), gruppiert nach Workspace. So sind die
 *     Systemlisten des Privatbereichs vollständig, egal in welchem Mandanten sie
 *     liegen (der Heim-WS allein ist oft leer/partiell).
 */

import { useCallback, useEffect, useState } from "react";

import { ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";
import { navigate } from "../router";

import "./ryfe.scss";

// Paket-Präfix → Anzeige-Gruppe (für eine geordnete Darstellung).
const PACKAGE_LABEL: Record<string, string> = {
  aufgaben: "Aufgaben & Prozesse",
  projekte: "Projekte",
  crm: "CRM",
  bookmarks: "Lesezeichen",
};

type SysList = { id: string; name: string; list_type: string };
// Aggregat-Zeile: eine Systemliste plus ihre Workspace-Herkunft.
type AggList = SysList & { workspace_id: string; workspace_name: string };

export const RyfeSystemLists = () => {
  const { workspace, workspaces, privateScope } = useRyfe();
  const wsId = workspace?.id;
  const [lists, setLists] = useState<SysList[] | null>(null);
  const [agg, setAgg] = useState<AggList[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toSys = (l: { id: string; name: string; list_type?: string | null }) =>
    ({ id: l.id, name: l.name, list_type: String(l.list_type) } as SysList);

  const load = useCallback(async () => {
    setError(null);
    try {
      if (privateScope) {
        // Cross-WS-Aggregation: je Workspace die gebundenen Systemlisten laden,
        // mit Workspace-Herkunft. Ein fehlschlagender WS kippt die Sicht nicht.
        const per = await Promise.all(
          (workspaces || []).map(async (w) => {
            try {
              const r = await ryfeApi.listWorkspaceLists(w.id);
              return r.lists
                .filter((l) => !!l.list_type)
                .map((l) => ({
                  ...toSys(l),
                  workspace_id: w.id,
                  workspace_name: w.name,
                }));
            } catch {
              return [];
            }
          }),
        );
        const flat = per.flat();
        flat.sort(
          (a, b) =>
            a.workspace_name.localeCompare(b.workspace_name) ||
            a.list_type.localeCompare(b.list_type),
        );
        setAgg(flat);
        return;
      }
      if (!wsId) {
        return;
      }
      const r = await ryfeApi.listWorkspaceLists(wsId);
      const sys = r.lists.filter((l) => !!l.list_type).map(toSys);
      sys.sort((a, b) => a.list_type.localeCompare(b.list_type));
      setLists(sys);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [privateScope, workspaces, wsId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openList = (id: string) => navigate(`/list/${id}`);

  // ---- Privatbereich: Aggregation nach Workspace gruppiert -----------------
  if (privateScope) {
    const byWs = new Map<string, AggList[]>();
    for (const l of agg || []) {
      const key = l.workspace_name;
      (byWs.get(key) || byWs.set(key, []).get(key)!).push(l);
    }
    return (
      <div className="ryfe-page">
        <h1 className="ryfe-page__title">Systemlisten — aggregiert</h1>
        <p className="ryfe-muted" style={{ padding: 0 }}>
          Die Systemlisten aller deiner Workspaces (Privatbereich). Ein Klick
          öffnet die Liste im jeweiligen Workspace.
        </p>

        {error && <div className="ryfe-notice ryfe-notice--warn">{error}</div>}

        {agg && agg.length === 0 && (
          <p className="ryfe-muted">
            In keinem deiner Workspaces wurden Systemlisten gefunden.
          </p>
        )}

        {[...byWs.entries()].map(([wsName, items]) => (
          <div className="ryfe-panel" key={wsName}>
            <h2 className="ryfe-panel__title">{wsName}</h2>
            <div className="ryfe-table-wrap">
              <table className="ryfe-table">
                <thead>
                  <tr>
                    <th>Liste</th>
                    <th>Typ</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((l) => (
                    <tr key={l.id} className="ryfe-row">
                      <td>
                        <button
                          className="ryfe-linkish"
                          onClick={() => openList(l.id)}
                        >
                          {l.name}
                        </button>
                      </td>
                      <td className="ryfe-muted">{l.list_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ---- Workspace-Scope: die Systemlisten des aktiven Workspace --------------
  const groups = new Map<string, SysList[]>();
  for (const l of lists || []) {
    const pkg = l.list_type.split(".")[0];
    const key = PACKAGE_LABEL[pkg] || pkg;
    (groups.get(key) || groups.set(key, []).get(key)!).push(l);
  }

  return (
    <div className="ryfe-page">
      <h1 className="ryfe-page__title">Systemlisten</h1>
      <p className="ryfe-muted" style={{ padding: 0 }}>
        Die Systemlisten dieses Workspace — Aufgaben, Prozesse, Projekte, CRM,
        Lesezeichen. Ein Klick öffnet die Liste.
      </p>

      {error && <div className="ryfe-notice ryfe-notice--warn">{error}</div>}

      {lists && lists.length === 0 && (
        <p className="ryfe-muted">
          Keine Systemlisten in diesem Workspace gefunden.
        </p>
      )}

      {[...groups.entries()].map(([label, items]) => (
        <div className="ryfe-panel" key={label}>
          <h2 className="ryfe-panel__title">{label}</h2>
          <div className="ryfe-table-wrap">
            <table className="ryfe-table">
              <thead>
                <tr>
                  <th>Liste</th>
                  <th>Typ</th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} className="ryfe-row">
                    <td>
                      <button
                        className="ryfe-linkish"
                        onClick={() => openList(l.id)}
                      >
                        {l.name}
                      </button>
                    </td>
                    <td className="ryfe-muted">{l.list_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};
